import { ButtonColor, ButtonSize, Table, TableRow } from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Customer } from 'src/front/api/apiInstance';

import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import { containsLatinFIO, splitCustomerName } from 'src/front/common/utils/formUtils';
import AsyncButton from 'src/front/components/AsyncButton';
import CustomerSearchForm, {
  customerSearchDefaultValues,
  SearchFormData,
} from 'src/front/components/CustomerSearchForm';

import {
  Container,
  Separator,
  Subtitle,
  TableWrapper,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';

import {
  useCardReissueState,
  useClearDispatch,
  useCustomerSearchDispatch,
} from 'src/front/contexts/CardReissueContext';

const customerTableHeaders = [
  'cardEntry_customerInfo_name_label',
  'cardEntry_customerInfo_middleName_label',
  'cardEntry_customerTable_birth_title',
  'cardEntry_customerInfo_gender_label',
  'cardEntry_customerTable_document_title',
];

interface Props {
  goToNextStep: () => void;
}

export interface CustomerTableRow extends TableRow {
  customerNumber?: string;
  customerSurname?: string;
  customerName?: string;
  customerSecondName?: string;
  customerSurnameNonresident?: string;
  customerNameNonresident?: string;
  customerSecondNameNonresident?: string;
  isResidentRF?: boolean;
}

const CardReissueCustomerSearch: FC<Props> = ({ goToNextStep }) => {
  const dispatch = useCustomerSearchDispatch();
  const clearCustomerSearch = useClearDispatch();

  const { customerSearchFormValues, customerSearchValues } = useCardReissueState();
  const { t } = useTranslation();

  const [searchFormValues, setSearchFormValues] = useState<SearchFormData>(
    customerSearchFormValues || customerSearchDefaultValues
  );

  const [customerList, setCustomerList] = useState<CustomerTableRow[]>();
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerTableRow | undefined>(
    customerSearchValues.selectedCustomer
  );
  const [customerPage, setCustomerPage] = useState<number>(
    customerSearchValues.customerPageNumber || 1
  );
  const [hasCustomerNextPage, setHasCustomerNextPage] = useState<boolean>(false);
  const [customersLoading, setCustomersLoading] = useState<boolean>(false);
  const [customerWarning, setCustomerWarning] = useState<string>();

  const loadCustomerList = useCallback(
    async (
      pageNumber = customerPage,
      forceRefresh = false,
      formValues: SearchFormData = searchFormValues
    ): Promise<void> => {
      try {
        setCustomersLoading(true);
        const { birthDate, ...rest } = formValues;
        const { data } = await Customer.getCustomers({
          ...rest,
          birthDate: formatDate(birthDate),
          rowCountPerPage: 5,
          pageNumber: pageNumber || customerPage,
          refresh: !pageNumber || forceRefresh,
        });

        const customers = data.customerList.map((customer) => ({
          id: customer.customerId || '',
          columns: [
            `${customer.customerName} ${customer.customerSurname}`,
            customer.customerSecondName || '',
            `${customer.birthDate || ''} - ${customer.birthPlace || ''}`,
            customer.gender || '',
            `${customer.identityDocumentSerialCode} - ${customer.identityDocumentSerialNumber}`,
          ],
          customerNumber: customer.customerNumber,
          customerSurnameNonresident: customer.customerSurnameNonresident,
          customerNameNonresident: customer.customerNameNonresident,
          customerSecondNameNonresident: customer.customerSecondNameNonresident,
          customerSurname: customer.customerSurname,
          customerName: customer.customerName,
          customerSecondName: customer.customerSecondName,
          isResidentRF: customer.isResidentRF,
        }));
        setCustomerList(customers);
        setHasCustomerNextPage(data.hasNextPage);
        setCustomerPage(pageNumber || 1);
        setCustomersLoading(false);
        setCustomerWarning(data.warning);
      } catch (error) {
        // TODO (mert.ohancan): show error on modal or handle with error boundary
      }
    },
    [customerPage, searchFormValues]
  );

  const handleSelectCustomer = (customer: CustomerTableRow | undefined): void => {
    clearCustomerSearch();
    if (!customer) {
      return;
    }
    if (
      !customer?.isResidentRF &&
      ((containsLatinFIO(customer.customerSurname) &&
        customer?.customerSurnameNonresident === '') ||
        (containsLatinFIO(customer.customerName) && customer?.customerNameNonresident === '') ||
        (containsLatinFIO(customer.customerSecondName) &&
          customer?.customerSecondNameNonresident === ''))
    ) {
      setSelectedCustomer(undefined);
      setCustomerWarning(t('cardReissues_customerSearch_fullNameRequired_error'));
    } else {
      setSelectedCustomer(customer);
      setCustomerWarning('');
    }
  };

  useEffect(() => {
    const loadPreviousValues = (): void => {
      loadCustomerList();
    };

    if (
      !customerList &&
      customerSearchValues.selectedCustomer.columns.length &&
      !customersLoading
    ) {
      loadPreviousValues();
    }
  }, [customerList, customerSearchValues, customersLoading, loadCustomerList]);

  const clearPreviousValues = (): void => {
    setCustomerList([]);
    setSelectedCustomer(undefined);
    setCustomerWarning('');
  };

  const onSubmit = async (formData: SearchFormData): Promise<void> => {
    setSearchFormValues(formData);
    clearPreviousValues();
    await loadCustomerList(1, true, formData);
  };
  const handleContinue = (): void => {
    if (selectedCustomer) {
      const [customerName, customerSecondName] = selectedCustomer.columns;
      dispatch({
        customerSearchFormValues: searchFormValues,
        customerSearchValues: {
          customerId: selectedCustomer.id,
          customerNumber: selectedCustomer.customerNumber || '',
          clientName: splitCustomerName(customerName, customerSecondName),
          selectedCustomer,
          customerPageNumber: customerPage,
        },
      });
      goToNextStep();
    }
  };

  return (
    <Container>
      <Title>
        <Trans i18nKey="cardReissue_title" />
      </Title>
      <Separator />
      <CustomerSearchForm formValues={searchFormValues} onSubmit={onSubmit} />
      {customerList ? (
        <>
          <Separator />
          <Subtitle>
            <Trans i18nKey="cardReissue_customerInfo_text" />
          </Subtitle>
          <TableWrapper>
            <Table
              emptyTableText={t('cardEntry_customerInfo_notFound_text')}
              hasNextPage={hasCustomerNextPage}
              headers={customerTableHeaders.map((head) => t(head))}
              items={customerList}
              loading={customersLoading}
              loadPageItems={loadCustomerList}
              onSelect={handleSelectCustomer}
              pageNumber={customerPage}
              selectedItem={selectedCustomer}
              clearItemOnPageChange
              withRadio
            />
          </TableWrapper>
          {selectedCustomer ? (
            <AsyncButton
              color={ButtonColor.Primary}
              disabled={customersLoading}
              onClick={handleContinue}
              size={ButtonSize.Medium}
              text={t('general_continue_button')}
            />
          ) : null}
          {customerWarning ? <WarningText>{customerWarning}</WarningText> : null}
        </>
      ) : null}
    </Container>
  );
};

export default CardReissueCustomerSearch;
